import { Alert, Group, Loader, Table, Text } from '@mantine/core';
import { useGetPreviousClaimBatchDetails } from 'api/resource/claim';
import { formatDateOnly } from 'utils/dateUtils';

const formatBatchDate = (isoValue: string | null | undefined) => {
  const formatted = formatDateOnly(isoValue);
  return formatted === 'N/A' ? '-' : formatted;
};

const formatTariffs = (
  tariffs: Array<{
    code: string;
    units?: number | null;
  }>
) => {
  if (!tariffs.length) return '-';
  return tariffs
    .map((tariff) =>
      tariff.units && tariff.units > 1
        ? `${tariff.code} x${tariff.units}`
        : tariff.code
    )
    .join(', ');
};

interface ClaimBatchSummaryContentProps {
  siteID: string;
  batchID: string;
  enabled: boolean;
}

export const ClaimBatchSummaryContent = ({
  siteID,
  batchID,
  enabled,
}: ClaimBatchSummaryContentProps) => {
  const { data, isLoading, isError } = useGetPreviousClaimBatchDetails({
    siteID,
    batchID,
    enabled,
  });

  if (isLoading) {
    return (
      <Group p="sm">
        <Loader size="xs" />
        <Text size="sm">Loading batch details...</Text>
      </Group>
    );
  }

  if (isError || !data) {
    return (
      <Alert color="red" variant="light" m="sm">
        Unable to load previous batches. Please try again.
      </Alert>
    );
  }

  return (
    <>
      <Group p="sm" gap="xl">
        <Text size="sm">
          <strong>Created Date:</strong> {formatBatchDate(data.createdAt)}
        </Text>
        <Text size="sm">
          <strong>Provider:</strong> {data.provider?.displayName || '-'}
        </Text>
        <Text size="sm">
          <strong>Claim Count:</strong> {data.claims.length}
        </Text>
      </Group>

      {!data.claims.length ? (
        <Text size="sm" c="dimmed" p="sm">
          No claims found for this batch.
        </Text>
      ) : (
        <Table striped highlightOnHover withTableBorder withColumnBorders m="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Patient</Table.Th>
              <Table.Th>Date of Service</Table.Th>
              <Table.Th>Claim Number</Table.Th>
              <Table.Th>Tariffs Applied</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.claims.map((claim) => (
              <Table.Tr key={claim.id}>
                <Table.Td>{claim.patientName}</Table.Td>
                <Table.Td>{formatBatchDate(claim.dateOfService)}</Table.Td>
                <Table.Td>{claim.claimNumber || '-'}</Table.Td>
                <Table.Td>{formatTariffs(claim.tariffs)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </>
  );
};
